/**
 * Authentication Guard
 * Protects routes that require authentication
 *
 * Note: This guard has a timeout to prevent blocking page load.
 */
import { useAuthStore } from '../../stores/auth';

// 타임아웃 래퍼
function withTimeout(promise, ms) {
    const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Guard timeout')), ms)
    );
    return Promise.race([promise, timeout]);
}

export async function authGuard(to, _from, next) {
    console.log(`[AuthGuard] Called for route: ${to.path}`);

    const authStore = useAuthStore();

    // Wait for auth initialization with timeout (max 3 seconds)
    if (!authStore.isInitialized) {
        console.log('[AuthGuard] Waiting for auth initialization (max 3s)...');
        try {
            await withTimeout(authStore.initialize(), 3000);
        } catch (error) {
            console.warn('[AuthGuard] Auth initialization timeout or failed:', error.message);
            // 타임아웃이어도 페이지는 계속 진행
        }
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const isGuestOnly = to.matched.some((record) => record.meta.guestOnly);

    // Redirect authenticated users away from guest-only pages (login, register)
    if (isGuestOnly && authStore.isAuthenticated) {
        const returnUrl = to.query.ReturnUrl || to.query.returnUrl;

        // ⚠️ 무한 리다이렉트 루프 방지 (2026-07-30):
        // returnUrl 이 /connect/* (authorize 재개 URL) 라는 건, Duende 가 세션이 있음에도
        // "상호작용(재인증) 필요" 로 판정해 로그인 UI 로 보냈다는 신호다
        //   (max_age 초과, prompt=login, consent 등).
        // 이때 세션 존재만 보고 returnUrl 로 자동 복귀하면 → authorize 가 또 재인증 요구 →
        // 로그인 페이지 → 무한 루프 (mission-web max_age=300, auth_time stale 시 실제 발생).
        // 따라서 /connect returnUrl 이면 자동 복귀하지 말고 로그인 폼을 표시(재인증)한다.
        if (returnUrl && (returnUrl.startsWith('/connect/') || returnUrl.startsWith('/.well-known/'))) {
            console.warn('[AuthGuard] 재인증 요구(/connect returnUrl) — 세션 있어도 로그인 폼 표시 (루프 방지)');
            next();
            return;
        }

        // 그 외(내부 SPA 경로)는 이미 로그인 상태이므로 홈/returnUrl 로 이동
        console.log('[AuthGuard] Already authenticated, redirecting away from guest page');
        next(returnUrl || '/');
        return;
    }

    if (requiresAuth && !authStore.isAuthenticated) {
        // Redirect to login with return URL
        const returnUrl = to.fullPath;
        console.log('[AuthGuard] Auth required but not authenticated, redirecting to login');
        next({
            path: '/account/login',
            query: { ReturnUrl: returnUrl },
        });
    } else {
        console.log('[AuthGuard] Proceeding to route');
        next();
    }
}
